'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Calendar,
  Clock,
  MapPin,
  Users,
  Video,
  Presentation,
  ArrowRight,
  Sparkles
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const upcomingEvents = [
  {
    id: 'dx-summit-2024',
    type: 'conference',
    title: '地域DXサミット 2024',
    description: '全国の地域DX推進者が集結。最新事例の共有と、これからの地域DXの在り方を議論します。',
    date: '2024年3月15日（金）',
    time: '10:00-17:00',
    location: '広島国際会議場',
    format: 'hybrid',
    capacity: '300名（会場） / 無制限（オンライン）',
    price: '無料',
    featured: true,
    tags: ['カンファレンス', '事例共有', 'ネットワーキング'],
    speakers: [
      { name: '山田太郎', title: '総務省 デジタル統括官' },
      { name: '佐藤花子', title: '広島県知事' },
      { name: '鈴木一郎', title: 'CodeFox CEO' }
    ]
  },
  {
    id: 'hands-on-workshop-202403',
    type: 'workshop',
    title: '自治体向けDX実践ワークショップ',
    description: '実際のシステムを使った体験型ワークショップ。窓口業務のデジタル化を1日で学べます。',
    date: '2024年3月22日（金）',
    time: '13:00-17:00',
    location: 'Digital Pride Hiroshima',
    format: 'onsite',
    capacity: '30名',
    price: '¥5,000',
    featured: false,
    tags: ['ワークショップ', '実践', '自治体DX']
  },
  {
    id: 'online-seminar-202403',
    type: 'webinar',
    title: '観光DX最前線 - データ活用で変わる観光戦略',
    description: '観光データの収集・分析・活用方法を、実例を交えて解説。観光DXの第一歩を踏み出しましょう。',
    date: '2024年3月28日（木）',
    time: '14:00-15:30',
    location: 'オンライン',
    format: 'online',
    capacity: '100名',
    price: '無料',
    featured: false,
    tags: ['ウェビナー', '観光DX', 'データ活用']
  }
]

const pastEvents = [
  {
    id: 'dx-bootcamp-202402',
    type: 'workshop',
    title: 'DXリーダー育成ブートキャンプ',
    date: '2024年2月16-17日',
    participants: '45名',
    materials: true,
    recording: true
  },
  {
    id: 'smart-agriculture-202402',
    type: 'webinar',
    title: 'スマート農業入門セミナー',
    date: '2024年2月8日',
    participants: '120名',
    materials: true,
    recording: true
  },
  {
    id: 'community-dx-202401',
    type: 'conference',
    title: '地域コミュニティDXフォーラム',
    date: '2024年1月25日',
    participants: '200名',
    materials: true,
    recording: false
  }
]

export function EventsContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-background py-16">
        <div
          className="animate-fade-up container"
        >
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4">学び、つながり、実践する</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              イベント・セミナー
            </h1>
            <p className="text-xl text-muted-foreground">
              地域DXの最新動向を学び、実践者とつながる。<br />
              オンライン・オフラインで開催中。
            </p>
          </div>
        </div>
      </section>

      {/* イベント一覧 */}
      <section className="py-16">
        <div className="container">
          <Tabs defaultValue="upcoming" className="space-y-8">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upcoming">開催予定</TabsTrigger>
              <TabsTrigger value="past">過去のイベント</TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="space-y-8">
              {/* 注目のイベント */}
              {upcomingEvents.filter(e => e.featured).map((event) => (
                <FadeUp
                  key={event.id}
                >
                  <Card className="overflow-hidden border-primary/20">
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-2 text-center">
                      <span className="text-sm font-semibold text-primary flex items-center justify-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        注目のイベント
                      </span>
                    </div>
                    <CardHeader>
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div>
                          <CardTitle className="text-2xl">{event.title}</CardTitle>
                          <CardDescription className="mt-2 text-base">
                            {event.description}
                          </CardDescription>
                        </div>
                        <Badge variant="outline" className="shrink-0">
                          {event.price}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>{event.date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>{event.time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>{event.location}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>{event.capacity}</span>
                        </div>
                      </div>

                      {event.speakers && (
                        <div>
                          <p className="text-sm font-semibold mb-3">登壇者</p>
                          <div className="grid sm:grid-cols-3 gap-3">
                            {event.speakers.map((speaker, idx) => (
                              <div key={idx} className="text-sm">
                                <p className="font-medium">{speaker.name}</p>
                                <p className="text-muted-foreground">{speaker.title}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        {event.tags.map((tag, idx) => (
                          <Badge key={idx} variant="secondary">{tag}</Badge>
                        ))}
                        {event.format === 'hybrid' && (
                          <Badge variant="outline">
                            <Video className="h-3 w-3 mr-1" />
                            ハイブリッド開催
                          </Badge>
                        )}
                      </div>

                      <Button size="lg" className="w-full sm:w-auto">
                        参加申し込み
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                </FadeUp>
              ))}

              {/* その他のイベント */}
              <div className="grid md:grid-cols-2 gap-6">
                {upcomingEvents.filter(e => !e.featured).map((event, index) => (
                  <FadeUp delay={index * 0.1}
                    key={event.id}
                  >
                    <Card className="h-full">
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <CardTitle>{event.title}</CardTitle>
                            <CardDescription className="mt-2">
                              {event.description}
                            </CardDescription>
                          </div>
                          <Badge variant="outline">{event.price}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>{event.date}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span>{event.time}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span>{event.location}</span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {event.tags.map((tag, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>

                        <Button className="w-full">
                          詳細・申し込み
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  </FadeUp>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="past" className="space-y-6">
              <p className="text-center text-muted-foreground mb-8">
                過去のイベントの資料や録画をご覧いただけます
              </p>
              
              <div className="grid gap-4">
                {pastEvents.map((event, index) => (
                  <FadeUp delay={index * 0.1}
                    key={event.id}
                  >
                    <Card>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">{event.title}</CardTitle>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <span>{event.date}</span>
                              <span>参加者: {event.participants}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {event.materials && (
                              <Button size="sm" variant="outline">
                                <Presentation className="h-4 w-4 mr-1" />
                                資料
                              </Button>
                            )}
                            {event.recording && (
                              <Button size="sm" variant="outline">
                                <Video className="h-4 w-4 mr-1" />
                                録画
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  </FadeUp>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* 定期開催プログラム */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">定期開催プログラム</h2>
            <p className="text-lg text-muted-foreground">
              毎月開催している人気プログラム
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'DX相談会',
                description: '地域DXの専門家が個別にアドバイス。具体的な課題解決の糸口を見つけます。',
                frequency: '毎週火曜日',
                icon: Users
              },
              {
                title: '事例共有会',
                description: '実践者が集まり、成功事例や失敗談を共有。リアルな知見が得られます。',
                frequency: '毎月第3金曜日',
                icon: Presentation
              },
              {
                title: 'オンライン勉強会',
                description: '最新技術やトレンドを学ぶ勉強会。初心者でも参加しやすい内容です。',
                frequency: '毎月第2・4水曜日',
                icon: Video
              }
            ].map((program, index) => {
              const Icon = program.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card>
                    <CardHeader>
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <CardTitle>{program.title}</CardTitle>
                      <CardDescription>{program.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary">{program.frequency}</Badge>
                        <Button variant="ghost" size="sm">
                          詳細
                          <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold mb-4">
              イベント情報を見逃さない
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              メールマガジンに登録して、最新のイベント情報や<br />
              地域DXの事例をお届けします。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="メールアドレス"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <Button>登録する</Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}